import { DEFAULT_FPS } from '~/Util'
import Asset from './Asset'
import { COLOR_PRIMARIES } from '~/constant'

/**
 * Represents asset of playable media content – like video or audio.
 *
 *         |<  mediaStart  >|
 *       0 .................. duration
 *         |   MediaAsset   |
 * 0 ............................. Inf
 *   |<       startTime         >|
 *
 * @memberOf Assets
 * @extends {Asset}
 */
class MediaAsset extends Asset {

  constructor(data) {
    if (new.target === MediaAsset) {
      throw new Error(`${new.target.name} can't be instantiated`)
    }

    const {
      mediaStart = 0,
      fpsNum = 0,
      fpsDenum = 0,
      muted = false,
      visible = true,
      volume = 1,
      size = 0,
      mediaFileDuration = null,
      mediaPlayerDuration = null,
      offsetLeft = 0,
      offsetRight = 0,
      codecsInfo = null,
      path = null,
      originalUrl = '',
      markIn = 0,
      markOut = 0,
      playerProgress = 0,
      startTimeCodeFrames = 0,
      dfFlag = 0,
      width = 0,
      height = 0,
      refCount = null,
      createTime = null,
      colorPrimaries = COLOR_PRIMARIES.BT709,
      videoEncoderParams = '',
      videoEncoderParsedParams = '',
      rotationDegrees = 0,
      ...rest
    } = data

    super(rest)
    this._fpsNum = fpsNum
    this._fpsDenum = fpsDenum
    this._codecsInfo = codecsInfo
    this._mediaStart = mediaStart
    this._muted = muted
    this._visible = visible
    this._size = size
    this._path = path
    this._originalUrl = originalUrl
    this._playerProgress = playerProgress
    this._startTimeCodeFrames = startTimeCodeFrames
    this._dfFlag = dfFlag
    this._colorPrimaries = colorPrimaries
    this._width = width
    this._height = height
    this._refCount = refCount
    this._createTime = createTime
    this._videoEncoderParams = videoEncoderParams
    this._videoEncoderParsedParams = videoEncoderParsedParams
    this._rotationDegrees = rotationDegrees
    // Assume that initial duration, with which item is created,
    // is duration of associated media file.
    this._mediaFileDuration = mediaFileDuration || this._duration
    this._mediaPlayerDuration = mediaPlayerDuration || mediaFileDuration || this._duration
    // This value is going to change along with `mediaStart`
    // (as if playback starts not from the beginning, clip max duration is less than entire media file)
    this._maxDuration = this._mediaFileDuration
    this._volume = volume

    this._offsetLeft = offsetLeft
    this._offsetRight = offsetRight

    this._markIn = markIn
    this._markOut = markOut
  }

  // ---
  /**
   * @returns {number | null}
  */
  get refCount() {
    return this._refCount
  }

  /**
   * @param {number} refCount
  */
  set refCount(refCount) {
    this._refCount = refCount
  }

  /**
   * @returns {string}
  */
  get videoEncoderParams() {
    return this._videoEncoderParams
  }

  /**
   * @returns {string}
  */
  get videoEncoderParsedParams() {
    return this._videoEncoderParsedParams
  }

  /**
   * @returns {number | null}
  */
  get createTime() {
    return this._createTime
  }

  /**
   * @param {number} createTime
  */
  set createTime(createTime) {
    this._createTime = createTime
  }

  /**
   * @returns {number}
   */
  get mediaFileDuration() {
    return this._mediaFileDuration
  }

  get mediaPlayerDuration() {
    return this._mediaPlayerDuration
  }

  /**
   * @param {number} duration
   */
  set mediaPlayerDuration(duration) {
    this._mediaPlayerDuration = duration
  }

  /**
   * Playback start position for associated media file.
   *
   * @returns {number}
   */
  get mediaStart() {
    return this._mediaStart
  }

  /**
   * @param {number} time
   */
  set mediaStart(time) {
    this._mediaStart = Math.min(this._mediaFileDuration, Math.max(time, 0))
    // if (this._maxDuration !== Infinity) {
    //   this._maxDuration = this._mediaFileDuration - this._mediaStart
    // }
  }

  /**
   * @returns {number}
   */
  get mediaEnd() {
    return this.mediaStart + this.duration
  }

  /**
 * @return {number}
 */
  get fpsNum() {
    return this._fpsNum || DEFAULT_FPS
  }

  /**
* @return {number}
*/
  get fpsDenum() {
    return this._fpsDenum || 1
  }

  /**
   * @return {number}
   */
  get fps() {
    return this.fpsNum / this.fpsDenum
  }

  /**
   * @return {string}
   */
  get codecsInfo() {
    return this._codecsInfo
  }

  // ---

  /**
   * @returns {number} [0-1]
   */
  get volume() {
    return this._volume
  }

  /**
   * @param {number} volume [0-1]
   */
  set volume(volume) {
    this._volume = volume
  }

  /**
   * @returns {boolean}
   */
  get muted() {
    return this._muted
  }

  /**
   * @param {boolean} muted
   */
  set muted(muted) {
    this._muted = muted
  }

  get visible() {
    return this._visible
  }

  /**
   * @param {boolean} visible
   */
  set visible(visible) {
    this._visible = visible
  }

  // ---

  /**
   * @returns {number}
   */
  get size() {
    return this._size
  }

  /**
   * @param {number} size
   */
  set size(size) {
    this._size = size
  }

  // ---

  /**
   * @returns {string}
   */
  get path() {
    return this._path
  }

  /**
   * @param {string} path
   */
  set path(path) {
    this._path = path
  }

  // ---

  /**
   * @returns {number}
   */
  get width() {
    return this._width
  }

  /**
   * @param {number} width
   */
  set width(width) {
    this._width = width
  }

  // ---

  /**
   * @returns {number}
   */
  get height() {
    return this._height
  }

  /**
   * @param {number} height
   */
  set height(height) {
    this._height = height
  }

  // ---

  /**
   * @returns {number}
   */
  get rotationDegrees() {
    return this._rotationDegrees
  }

  /**
   * @param {number} rotationDegrees
   */
  set rotationDegrees(rotationDegrees) {
    this._rotationDegrees = rotationDegrees
  }

  /**
   * @returns {number}
   */
  get offsetLeft() {
    return this._offsetLeft
  }

  /**
   * @param {number} offsetLeft
   */
  set offsetLeft(offsetLeft) {
    this._offsetLeft = offsetLeft
  }

  /**
   * @returns {string}
   */
  get originalUrl() {
    return this._originalUrl
  }

  /**
    * @param {number} originalUrl
    */
  set originalUrl(originalUrl) {
    this._originalUrl = originalUrl
  }

  /**
   * @returns {number}
  */
  get offsetRight() {
    return this._offsetRight
  }

  /**
   * @param {number} offsetRight
  */
  set offsetRight(offsetRight) {
    this._offsetRight = offsetRight
  }

  /**
   * @returns {number}
  */
  get markIn() {
    return this._markIn
  }

  /**
   * @param {number} markIn
  */
  set markIn(markIn) {
    this._markIn = markIn
  }

  /**
   * @returns {number}
  */
  get markOut() {
    return this._markOut
  }

  /**
   * @param {number} markOut
  */
  set markOut(markOut) {
    this._markOut = markOut
  }

  /**
   * @returns {number}
  */
  get playerProgress() {
    return this._playerProgress
  }

  /**
    * @param {number} playerProgress
  */
  set playerProgress(playerProgress) {
    this._playerProgress = playerProgress
  }

  /**
   * @returns {number}
  */
  get startTimeCodeFrames() {
    return this._startTimeCodeFrames
  }

  /**
   * @returns {number}
  */
  get dfFlag() {
    return this._dfFlag
  }

  /**
   * @returns {string}
  */
  get colorPrimaries() {
    return this._colorPrimaries
  }

  _truncate(at, delta, leftSide) {
    super._truncate(at, delta, leftSide)
    if (leftSide) {
      this.mediaStart += delta
    }
  }

}

export default MediaAsset
